import { useMemo, useState } from "react";
import { estimateRevenue, estimateHours, formatINR } from "@/lib/discovery";

const DEFAULTS = { leads: 1000, conversion: 3, value: 50000, followUp: 60, hoursPerWeek: 20 };

function Field({ id, label, prefix, suffix, value, onChange, step = 1, min = 0, max }) {
  return (
    <div>
      <label htmlFor={id} className="eyebrow block mb-2">{label}</label>
      <div className="flex items-center border border-ink bg-paper focus-within:outline focus-within:outline-2 focus-within:outline-cobalt focus-within:outline-offset-2">
        {prefix && <span className="pl-3 text-ink/60" aria-hidden="true">{prefix}</span>}
        <input
          id={id} type="number" inputMode="decimal" value={value} min={min} max={max} step={step}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-3 py-2.5 text-lg outline-none tabular-nums focus-visible:ring-2 focus-visible:ring-cobalt focus-visible:ring-offset-0"
        />
        {suffix && <span className="pr-3 text-ink/60" aria-hidden="true">{suffix}</span>}
      </div>
    </div>
  );
}

/** A business tool, not a calculator: current, with improvement, the gap. */
export default function ImpactEstimate({ hasRevenueOpp = true }) {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((c) => ({ ...c, [k]: val }));
  const rev = useMemo(() => estimateRevenue(v), [v]);
  const hrs = useMemo(() => estimateHours(v), [v]);
  const barMax = Math.max(rev.withImprovement, 1);

  return (
    <div className="border border-ink" data-testid="impact-estimate">
      <div className="border-b border-ink px-5 py-3 flex flex-wrap justify-between gap-2">
        <p className="eyebrow">Impact estimate</p>
        <p className="text-[12px] text-ink/55">Illustrative estimate, based on your inputs</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-5 p-5 md:p-6 border-b lg:border-b-0 lg:border-r border-ink grid gap-5">
          {hasRevenueOpp && (
            <>
              <Field id="ie-leads" label="Monthly leads or enquiries" value={v.leads} onChange={set("leads")} step={10} />
              <Field id="ie-conv" label="Current conversion" suffix="%" value={v.conversion} onChange={set("conversion")} step={0.5} max={100} />
              <Field id="ie-value" label="Average customer value" prefix="₹" value={v.value} onChange={set("value")} step={1000} />
              <Field id="ie-fu" label="Leads that get a proper follow-up" suffix="%" value={v.followUp} onChange={set("followUp")} step={5} min={1} max={100} />
            </>
          )}
          <Field id="ie-hours" label="Team hours a week on the work you picked" suffix="hrs" value={v.hoursPerWeek} onChange={set("hoursPerWeek")} step={1} />
        </div>

        <div className="lg:col-span-7 p-5 md:p-6 grid gap-8" aria-live="polite">
          {hasRevenueOpp && (
            <div>
              <p className="eyebrow mb-4">Revenue, per month</p>
              <Bar label="Current" value={rev.current} max={barMax} tone="ink" />
              <Bar label="With follow-up coverage at 95%" value={rev.withImprovement} max={barMax} tone="cobalt" />
              <div className="mt-5 flex items-baseline gap-4 flex-wrap">
                <span className="eyebrow">Potential gap</span>
                <span className="numeral text-[44px] md:text-[56px] leading-none tabular-nums" data-testid="impact-gap">{formatINR(rev.gap)}</span>
                <span className="text-[13px] text-ink/60">a month, if conversion rises from {v.conversion}% to about {rev.improvedConversion}%</span>
              </div>
            </div>
          )}

          <div>
            <p className="eyebrow mb-4">Time, per year</p>
            <div className="grid grid-cols-3 gap-3 text-center border border-ink">
              <Cell k="Hours spent" val={`${Math.round((Number(v.hoursPerWeek) || 0) * 48).toLocaleString("en-IN")}`} />
              <Cell k={`Automated (${hrs.share}%)`} val={`${hrs.yearly.toLocaleString("en-IN")}`} tone="cobalt" />
              <Cell k="Hours recovered" val={`${hrs.yearly.toLocaleString("en-IN")}`} tone="cobalt" />
            </div>
          </div>

          <p className="text-[12px] leading-relaxed text-ink/55">
            Assumptions, stated plainly: leads that never got a follow-up did not convert, so
            conversion rises with follow-up coverage, capped at 1.5x today's rate. About half
            of a repetitive job can usually be automated. 48 working weeks. We quote a real
            number only after seeing the actual workflow.
          </p>
        </div>
      </div>
    </div>
  );
}

function Bar({ label, value, max, tone }) {
  const w = Math.max(2, Math.round((value / max) * 100));
  return (
    <div className="mb-3">
      <div className="flex justify-between text-[13px] mb-1"><span>{label}</span><span className="tabular-nums font-medium">{formatINR(value)}</span></div>
      <div className="h-3 border border-ink/40 bg-paper">
        <div className={`h-full ${tone === "cobalt" ? "bg-cobalt" : "bg-ink/60"} transition-[width] duration-700 motion-reduce:transition-none`} style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

function Cell({ k, val, tone }) {
  return (
    <div className="p-3 border-r border-ink last:border-r-0">
      <p className={`numeral text-[28px] md:text-[34px] leading-none tabular-nums ${tone === "cobalt" ? "" : "text-ink"}`} style={tone === "cobalt" ? undefined : { color: "#16130F" }}>{val}</p>
      <p className="text-[11px] text-ink/60 mt-1.5 uppercase tracking-[0.08em]">{k}</p>
    </div>
  );
}
